import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { ActionButton } from '@zeus/core/layout/MainLayout';
import { NEW_PAGE_ID } from '_/common/routes/routes.constants';
import { IProjectRouteParams } from '_/common/routes/routes.types';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { getFlowsBaseUrl } from '_/features/flows/common/utils/flowUrl.utils';
import { flowListAdapter } from '_/features/flows/list/flowList.adapter';
import { IFlowListRow } from '_/features/flows/list/flowList.types';
import { useFlowList } from '_/hooks/useFlows';
import useOrgAccess from '_/hooks/useOrgAccess';
import { compareSort } from '_/utils/formatUtils';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Spin, Input, Space } from 'antd';
import { ColumnType } from 'antd/lib/table';
import React, { ReactElement, useCallback, useMemo, useEffect, useState } from 'react';

const columnDefs: ColumnType<IFlowListRow>[] = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: compareSort('name'),
    render: (name, { href }: IFlowListRow) => (
      <Link to={href} className="wb-textDecoration-underline">
        {name}
      </Link>
    ),
  },
  {
    title: 'Slug',
    dataIndex: 'slug',
    key: 'slug',
    sorter: compareSort('slug'),
  },
  {
    title: 'Type',
    dataIndex: 'calcType',
    key: 'calcType',
    sorter: compareSort('calcType'),
  },
  {
    dataIndex: 'createdOn',
    key: 'addedAt',
    sorter: compareSort('addedAt'),
    title: 'Created On',
  },
];

const FlowList = (): ReactElement => {
  const history = useNavigate();
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const params = useParams({ strict: false }) as Partial<IProjectRouteParams>;
  const projectSlug = params.projectSlug || '';
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();
  const { selectedProject } = useProject();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const flowsUrl = getFlowsBaseUrl({ orgSlug: selectedOrgSlug || '', projectSlug: projectSlug });
  const [searchText, setSearchText] = useState('');

  // React Query hook - returns raw flows
  const { data: rawFlows = [], isLoading: isFetching, isRefetching, refetch: fetchFlows } = useFlowList(selectedProject?.id);

  // Transform flows to add hrefs using context values
  const flowList = useMemo(() => {
    if (!selectedOrgSlug || !projectSlug) return [];
    return flowListAdapter(rawFlows, selectedOrgSlug, projectSlug);
  }, [rawFlows, selectedOrgSlug, projectSlug]);

  // Filter flows based on search text
  const filteredFlowList = useMemo(() => {
    if (!searchText) return flowList;
    const searchLower = searchText.toLowerCase();
    return flowList.filter((flow) => {
      return (
        flow.name?.toLowerCase().includes(searchLower) ||
        flow.slug?.toLowerCase().includes(searchLower) ||
        flow.calcType?.toLowerCase().includes(searchLower)
      );
    });
  }, [flowList, searchText]);

  const addFlow = useCallback(() => {
    void history({ to: `${flowsUrl}/${NEW_PAGE_ID}` });
  }, [history, flowsUrl]);

  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        onClick: addFlow,
        icon: <PlusCircleOutlined />,
        label: 'Add Flow',
        disabled: !access,
      },
    ],
    [addFlow, access],
  );

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${projectSlug}/home`,
      },
      { title: 'Flows' },
    ]);
    setActionButtons(actionButtons);
    setOnRefresh(fetchFlows);
    setRefreshLoading(isRefetching);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    setOnRefresh,
    setRefreshLoading,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    projectSlug,
    actionButtons,
    fetchFlows,
    isRefetching,
  ]);

  if (isFetching) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Input
        placeholder="Search flows..."
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ width: 300 }}
        allowClear
      />
      <Table columns={columnDefs} dataSource={filteredFlowList} pagination={false} rowKey="id" />
    </Space>
  );
};

export default FlowList;
